import { z } from 'zod';
import { idSchema } from './common.js';
/**
 * Background jobs (RELAY-936): one BullMQ queue per outbound integration, plus a sample
 * queue the staging smoke test uses and ONE dead-letter queue every other queue drains
 * into once a job has exhausted its attempts. Names and payloads live here so the API
 * (producer) and the worker (consumer) agree at compile time.
 */
export const INTEGRATIONS = [
    'recruitcrm',
    'stripe',
    'quickbooks',
    'slack',
    'sample',
    // Pipeline triggers' http_request sends: Postgres schedules, this queue sends.
    'triggers',
];
export const integrationSchema = z.enum(INTEGRATIONS);
// Hyphen, not colon: BullMQ uses ':' as its Redis key separator and rejects it in a queue name.
export const queueName = (integration) => `integration-${integration}`;
export const DEAD_LETTER_QUEUE = 'dead-letter';
export const QUEUE_NAMES = [...INTEGRATIONS.map(queueName), DEAD_LETTER_QUEUE];
/** Every job carries who/where it is for; the worker scopes its work with it. */
export const jobEnvelopeSchema = z.object({
    organisationId: idSchema,
    requestId: z.string().min(1).optional(),
});
/** The sample job: echoes a message, fails on demand (to exercise retry + dead-letter). */
export const sampleJobSchema = jobEnvelopeSchema.extend({
    message: z.string().min(1).max(500),
    /** Fail this many times before succeeding (0 = succeed first time). >= attempts ⇒ dead-letter. */
    failTimes: z.number().int().min(0).max(10).default(0),
});
/**
 * A pipeline trigger run to send. The run row in `trigger_runs` holds everything else;
 * the job is only the pointer, so a retry from the dead-letter panel re-reads the current trigger.
 */
export const triggerRunJobSchema = jobEnvelopeSchema.extend({ runId: idSchema });
/** An `http_request` send, then the dead-letter queue; `jobId` = run id, so a second enqueue is a no-op. */
export const TRIGGER_RUN_JOB_OPTIONS = {
    attempts: 8,
    backoff: { type: 'exponential', delay: 30_000 },
    // A kept completed job would swallow the loop's next enqueue of a run handed back to it.
    removeOnComplete: true,
    removeOnFail: false,
};
/**
 * How long the loop leaves an enqueued run alone (its `next_attempt_at` lease): far beyond the
 * job's whole retry budget, so only a job Redis lost is ever enqueued again.
 */
export const TRIGGER_RUN_QUEUE_LEASE_MS = 25 * 60 * 60 * 1000;
/** Shape of a dead-lettered job: the original plus why it died. */
export const deadLetterJobSchema = z.object({
    queue: z.string(),
    name: z.string(),
    data: z.unknown(),
    attemptsMade: z.number().int(),
    failedReason: z.string(),
    failedAt: z.iso.datetime({ offset: true }),
    originalJobId: z.string().nullable(),
});
/**
 * REDIS_URL → the connection BullMQ is given, for the API (producer) and the worker
 * (consumer) alike, so both always reach the same Redis database. The path is the database
 * index (`redis://host:6379/6` → db 6; none or `/` → Redis's default, 0), which is how two
 * local workers are kept off each other's queues.
 */
export function queueConnectionFromUrl(redisUrl) {
    const u = new URL(redisUrl);
    // The path as written: `u.pathname` resolves dot segments, so `/6/..` would read as `/`.
    const path = /^[^:]+:\/\/[^/?#]*\/?([^?#]*)/.exec(redisUrl)?.[1] ?? '';
    const db = Number(path);
    // The value can carry a password, so the message names the variable, never the URL.
    // isSafeInteger: a long run of digits would otherwise become Infinity or a rounded index.
    if (path !== '' && (!/^\d+$/.test(path) || !Number.isSafeInteger(db)))
        throw new Error('REDIS_URL: the path must be a database index (a non-negative integer)');
    return {
        host: u.hostname,
        port: Number(u.port || 6379),
        ...(path ? { db } : {}),
        ...(u.username ? { username: decodeURIComponent(u.username) } : {}),
        ...(u.password ? { password: decodeURIComponent(u.password) } : {}),
        ...(u.protocol === 'rediss:' ? { tls: {} } : {}),
        // BullMQ requires this for blocking commands.
        maxRetriesPerRequest: null,
    };
}
/** Retry policy every integration queue uses unless it overrides it. */
export const DEFAULT_JOB_OPTIONS = {
    attempts: 3,
    backoff: { type: 'exponential', delay: 1_000 },
    removeOnComplete: 1_000,
    removeOnFail: false, // kept until the dead-letter mover has copied it
};
/**
 * The RecruitCRM sync job (TS-1835): one CRM record to write to RecruitCRM. Produced by the
 * worker's outbox drain from the record's events and consumed by the `integration-recruitcrm`
 * processor, which reads the record fresh — the event only says WHICH record changed.
 * `eventName` is the event that queued it (a restore is dead-lettered, never synced).
 *
 * `project_stage` (TS-1878) is one project in ONE pipeline: `id` is the project and `pipelineId`
 * the pipeline its stage moved in (or that it joined). It is synced through that pipeline's
 * `pipeline_identities` link — Sales → the RCRM deal's stage — and needs `pipelineId`, which no
 * other entity carries.
 */
export const RECRUITCRM_SYNC_ENTITIES = [
    'candidate',
    'company',
    'contact',
    'job',
    'note',
    'project_stage',
];
export const recruitcrmSyncJobSchema = jobEnvelopeSchema
    .extend({
    entity: z.enum(RECRUITCRM_SYNC_ENTITIES),
    id: idSchema,
    eventId: idSchema,
    eventName: z.string().min(1),
    pipelineId: idSchema.optional(),
})
    .superRefine((job, ctx) => {
    if ((job.entity === 'project_stage') !== (job.pipelineId !== undefined))
        ctx.addIssue({
            code: 'custom',
            path: ['pipelineId'],
            message: 'required for project_stage, and only there',
        });
});
/**
 * The RecruitCRM queue's retry policy (user, 2026-10-28): 5 attempts, exponential backoff from
 * 10 s (10, 20, 40, 80 s — about 2.5 minutes before the dead-letter list), so a short RecruitCRM
 * outage or rate-limit burst does not dead-letter every queued record. Every path that puts a job
 * on `integration-recruitcrm` uses it: the worker's producer and re-queue, and the API's
 * dead-letter retry (through `jobOptionsFor`). The other queues keep `DEFAULT_JOB_OPTIONS`.
 */
export const RECRUITCRM_JOB_OPTIONS = {
    ...DEFAULT_JOB_OPTIONS,
    attempts: 5,
    backoff: { type: 'exponential', delay: 10_000 },
};
/** The default job options of a queue: its own policy for RecruitCRM and triggers, else the default. */
export function jobOptionsFor(queue) {
    if (queue === queueName('recruitcrm'))
        return RECRUITCRM_JOB_OPTIONS;
    return queue === queueName('triggers') ? TRIGGER_RUN_JOB_OPTIONS : DEFAULT_JOB_OPTIONS;
}
//# sourceMappingURL=jobs.js.map